'use client';

import * as React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useDirection } from '@/components/ui/direction';
import {
  clampWidth,
  nextWidthFromDrag,
  nextWidthFromKey,
  panelCssVar,
  panelWidthValue,
  type PanelBounds,
  type PanelEdge,
  type PanelId
} from '@/lib/panel-resize';

// Re-exported so a consumer needs only one import to use the hook.
export type { PanelId };

type PanelWidthState = {
  widths: Partial<Record<PanelId, number>>;
  setWidth: (panelId: PanelId, width: number) => void;
};

export const usePanelWidthStore = create<PanelWidthState>()(
  persist(
    (set) => ({
      widths: {},
      setWidth: (panelId, width) =>
        set((state) => ({ widths: { ...state.widths, [panelId]: width } }))
    }),
    { name: 'panel-widths', skipHydration: true }
  )
);

export type PanelResizeHandleProps = {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
  'aria-valuenow': number;
  'aria-valuemin': number;
  'aria-valuemax': number;
  'data-resizing': '' | undefined;
};

export function usePanelWidth(
  panelId: PanelId,
  {
    defaultWidth,
    min,
    max,
    edge,
    onCommit
  }: {
    defaultWidth: number;
    min: number;
    max: number;
    edge: PanelEdge;
    /** Runs on commit only, never per frame. Used to mirror to a cookie. */
    onCommit?: (width: number) => void;
  }
) {
  const dir = useDirection();
  const stored = usePanelWidthStore((state) => state.widths[panelId]);
  const setStoredWidth = usePanelWidthStore((state) => state.setWidth);
  const [isResizing, setIsResizing] = React.useState(false);

  // Matches use-sidebar-groups.ts: the store is created with
  // `skipHydration`, so nothing reads localStorage until after mount and
  // the server render stays deterministic.
  React.useEffect(() => {
    void usePanelWidthStore.persist.rehydrate();
  }, []);

  const bounds = React.useMemo<PanelBounds>(() => ({ min, max }), [min, max]);
  const cssVar = panelCssVar(panelId);

  // Clamped on READ as well as on write, so a hand-edited localStorage
  // entry cannot blank the layout or produce a 4000px sidebar.
  const width = clampWidth(stored ?? defaultWidth, bounds);
  const widthValue = panelWidthValue(panelId, defaultWidth);

  const paint = React.useCallback(
    (next: number) => {
      document.documentElement.style.setProperty(cssVar, `${next}px`);
    },
    [cssVar]
  );

  // Paint the stored width once it has rehydrated, so the variable and the
  // React-rendered fallback agree from the first commit onwards.
  React.useEffect(() => {
    paint(width);
  }, [paint, width]);

  const commit = React.useCallback(
    (next: number) => {
      setStoredWidth(panelId, next);
      onCommit?.(next);
    },
    [onCommit, panelId, setStoredWidth]
  );

  const dragRef = React.useRef<{
    startX: number;
    startWidth: number;
    latest: number;
    moved: boolean;
  } | null>(null);

  // Drag-time document state: the cursor and text selection, plus the flag
  // that suppresses the sidebar's own width transition. shadcn animates that
  // width over 200ms so collapsing glides — applied to every frame of a drag
  // it made the panel trail the pointer, which reads as lag rather than as an
  // animation. See the `[data-panel-resizing]` rule in globals.css.
  const beginDocumentDrag = React.useCallback(() => {
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.documentElement.dataset.panelResizing = '';
  }, []);

  const releaseBodyCursor = React.useCallback(() => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    delete document.documentElement.dataset.panelResizing;
  }, []);

  // An unmount mid-drag would otherwise leave the whole document stuck
  // with a col-resize cursor and text selection disabled.
  React.useEffect(
    () => () => {
      if (dragRef.current) releaseBodyCursor();
    },
    [releaseBodyCursor]
  );

  const handleProps = React.useMemo<PanelResizeHandleProps>(
    () => ({
      onPointerDown: (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
          startX: event.clientX,
          startWidth: width,
          latest: width,
          moved: false
        };
        setIsResizing(true);
        beginDocumentDrag();
      },
      onPointerMove: (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const next = nextWidthFromDrag({
          startWidth: drag.startWidth,
          startX: drag.startX,
          currentX: event.clientX,
          edge,
          dir,
          bounds
        });
        if (next !== drag.latest) drag.moved = true;
        drag.latest = next;
        paint(next);
      },
      onPointerUp: (event) => {
        const drag = dragRef.current;
        if (!drag) return;
        dragRef.current = null;
        setIsResizing(false);
        releaseBodyCursor();
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (drag.moved) commit(drag.latest);
      },
      onPointerCancel: () => {
        const drag = dragRef.current;
        if (!drag) return;
        dragRef.current = null;
        setIsResizing(false);
        releaseBodyCursor();
        // A cancelled drag is an abandoned one: repaint the last
        // committed width rather than keeping where the pointer died.
        paint(width);
      },
      onKeyDown: (event) => {
        const next = nextWidthFromKey({
          width,
          key: event.key,
          edge,
          dir,
          bounds,
          defaultWidth
        });
        if (next === null) return;
        event.preventDefault();
        paint(next);
        commit(next);
      },
      onDoubleClick: () => {
        const next = clampWidth(defaultWidth, bounds);
        paint(next);
        commit(next);
      },
      'aria-valuenow': width,
      'aria-valuemin': min,
      'aria-valuemax': max,
      'data-resizing': isResizing ? '' : undefined
    }),
    [
      beginDocumentDrag,
      bounds,
      commit,
      defaultWidth,
      dir,
      edge,
      isResizing,
      max,
      min,
      paint,
      releaseBodyCursor,
      width
    ]
  );

  const reset = React.useCallback(() => {
    const next = clampWidth(defaultWidth, bounds);
    paint(next);
    commit(next);
  }, [bounds, commit, defaultWidth, paint]);

  return { width, cssVar, widthValue, isResizing, handleProps, reset };
}
